<template>
  <div class="paginator" ref="root" :class="{ 'overflow-y-auto': $props.count !== 0 }" v-resize-observer="onResize">
    <div class="absolute h-full invisible" ref="rootabsolute"></div>
    <div :style="{ 'max-height': maxHeight || '90vh', 'height': maxHeight }">
      <table :class="{ 'min-h-full': !paginatorEnabled }">

        <thead v-if="'thead' in $slots">
          <slot name="thead"></slot>
          <tr>
            <th colspan="100" class="bg-gray-300 !h-[1px] !max-h-[1px] !py-0 !my0"></th>
          </tr>
        </thead>

        <tbody valign="top" class="relative">
          <!-- tbody - overlay -->
          <template v-if="$props.overlay && (!init || loading)">
            <slot v-if="'overlay' in $slots" name="overlay"></slot>
            <div v-else class="absolute w-full h-full n-loading-background flex justify-center items-center z-10">
              <span title="Actualiser les données." class="text-white w-1/5 h-1/5">
                <ArrowPathIcon v-if="loading" class="animate-spin inline-block h-full w-full" />
                <ArrowPathIcon v-else class="cursor-pointer inline-block h-full w-full" @click="firstload" />
              </span>
            </div>
          </template>
          <!-- tbody - slot -->
          <slot name="tbody" :loading="loading" :dataCount="dataCount" :init="!!init"></slot>
          <!-- tbody - infinite scroll observer -->
          <tr v-if="!paginatorEnabled">
            <td class="h-full" colspan="100">
              <div class="w-full flex items-end justify-center gap-x-20 mt-6 mb-2" :class="{ 'invisible': !init }"
                v-intersection-observer="[onInfiniteScroll, { threshold: 0.8, immediate: true }]">
                <div v-if="dataCount < $props.count" class="loader"></div>
                <div v-else class="border-b-2 w-1/2"></div>
              </div>
            </td>
          </tr>
        </tbody>
        <tfoot style="inset-block-end: 0; bottom: -5px">
          <!-- tfoot - pager -->
          <tr>
            <td class="pb-2" colspan="100">
              <div
                class="bg-white border-t border-gray-200 px-4 py-3 flex flex-wrap sm:flex-nowrap items-center justify-between sm:px-6"
                aria-label="Pagination">
                <template v-if="paginatorEnabled">
                  <div class="w-full sm:w-1/2 mb-4 sm:mb-0">
                    <p class="text-sm text-gray-700">
                      <span class="font-medium">{{ Math.min(((pageNumber - 1) * pageSize) + 1, $props.count) }}</span>
                      à
                      <span class="font-medium">{{ Math.min(pageNumber * pageSize, $props.count) }}</span>
                      sur
                      <span class="font-medium">{{ $props.count }} résulats</span>
                    </p>
                  </div>
                  <div class="text-gray-700 text-center text-sm">
                    taille de la page:
                    <input v-model.lazy.number="pageSize" type="number" min="1"
                      class="w-12 border rounded text-center" />
                  </div>
                  <div class="pl-4 text-gray-700 text-center text-sm">
                    aller à la page:
                    <input v-model.lazy.number="pageNumber" type="number" min="1"
                      class="w-12 border rounded text-center" />
                  </div>
                  <div class="flex-1 flex justify-between sm:justify-end">
                    <PaginatorPager v-model:page="pageNumber" :count="$props.count" :page-size="pageSize" />
                  </div>
                </template>
                <template v-else>
                  <div class="w-full sm:w-1/2 mb-4 sm:mb-0">
                    <p class="text-sm text-gray-700">
                      <span class="font-medium">{{ $props.count }} résulats</span>
                    </p>
                  </div>
                </template>

                <div v-if="enabled !== null" class="flex items-center justify-center"
                  :title="paginatorEnabled ? 'désactiver la pagination' : 'activer la pagination'">
                  <t-switch v-model="paginatorEnabled" size="s" class="!m-0" />
                </div>

              </div>
            </td>
          </tr>
          <!-- tfoot - slot -->
          <tr v-if="$slots.tfoot">
            <slot name="tfoot"></slot>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script lang="ts">
import PaginatorPager from './PaginatorPager.vue'
import { ArrowPathIcon } from '@heroicons/vue/24/solid'

const DEBUG_LOG = false
const DEBUG_LOG_INCREASE_PAGE = false
const MAX_INCREASE_PAGE_SIZE = 6

export default defineComponent({
  props: {

    /**
     * init value:
     *  - 0 to disable auto load
     * set value:
     *  - null to load / reload (page 1)
     *  - NaN  to reload all data displayed
     */
    count: Number,

    /** initial page size. null to load all data */
    size: { type: Number, default: () => 10 },

    /** if you add static elements */
    offset: { type: Number, default: () => 0 },

    /** enabled: paginator + disabled: infinite scroll */
    enabled: { type: Boolean, default: null },

    /** display overlay on init or loading (default: true) */
    overlay: { type: Boolean, default: () => true },

    /**
     * Don't use %
     * if not defined, component try to auto set
     * ex: 40vh / 900px
     */
    maxHeight: String,
  },
  components: { PaginatorPager, ArrowPathIcon },
  emits: {
    load: (o: PaginatorLoad) => true,
    'update:enabled': (o: boolean) => true,
    infinitescroll: () => true
  },
  slots: Object as SlotsType<{
    thead: {};
    tbody: { init: boolean; loading: boolean; dataCount: null | number; };
    overlay: { init: boolean; loading: boolean; };
    tfoot: {};
  }>,
  setup(props, { emit }) {

    const pageNumber = ref(1)
    const initialPageSize = props.size
    const pageSize = ref(props.size)

    const loading = ref(false)
    const dataCount = ref<number>(null)
    const init = ref<boolean>(null)

    // usefull to lock checkPage on resize event + limit number of increase page
    const increasePageSize = ref(0)

    const root = useTemplateRef<HTMLDivElement>('root')
    function haveScroll() {
      if (!root.value) return false
      return root.value.scrollHeight !== root.value.clientHeight && (root.value.scrollHeight - root.value.clientHeight >= 15)
    }

    function emitLoad(_limit: number, _offset: number, delta: boolean, onComplete?: () => Promise<void>) {
      if (!delta)
        loading.value = true

      const limit = (props.offset && _offset === 0) ? _limit - props.offset : _limit
      const offset = (props.offset && _offset !== 0) ? _offset - props.offset : _offset
      if (DEBUG_LOG)
        console.debug('emit load', offset, '->', limit + offset, delta ? '(delta)' : '(no delta)', 'pageSize:', pageSize.value)

      emit('load', {
        limit,
        offset,
        delta,
        complete(dataLength) {
          loading.value = false
          dataCount.value = dataLength
          if (!init.value) init.value = true

          if (onComplete)
            nextTick(async () => { if (onComplete) await onComplete(); })
        }
      })
    }

    async function checkPageSize() {
      // loading
      if (props.count === undefined || props.count === null || isNaN(props.count))
        return

      // no data
      if (props.count === 0)
        return

      // not enough data loaded => increase page size
      if (dataCount.value < props.count && increasePageSize.value < MAX_INCREASE_PAGE_SIZE && !(haveScroll())) {

        if (DEBUG_LOG || DEBUG_LOG_INCREASE_PAGE)
          console.debug(`checkPageSize[${increasePageSize.value}] - increasePageSize ${pageSize.value} => ${pageSize.value + initialPageSize} - count ${props.count} data ${dataCount.value} haveScroll ${haveScroll()}`)

        increasePageSize.value += 1
        const _pp = pageSize.value
        pageSize.value += initialPageSize
        emitLoad(
          initialPageSize,
          _pp,
          true,
          async () => { if (!await checkPageSize()) increasePageSize.value = 0; }
        )
        return true
      } else if (DEBUG_LOG) {
        console.debug(`checkPageSize[${increasePageSize.value}] count ${props.count} data ${dataCount.value} haveScroll ${haveScroll()}`)
      }
    }

    function reload(_pageNumber: number, _pageSize: number) {
      if (DEBUG_LOG)
        console.debug('reload', _pageNumber, _pageSize)
      return new Promise<void>(r => {
        emitLoad(
          _pageSize,
          (_pageNumber - 1) * _pageSize,
          false,
          async () => { checkPageSize(); r(); }
        )
      })
    }

    const paginatorEnabled = ref(props.enabled)
    watch([() => props.enabled, paginatorEnabled], ([_propsEnabled, _refEnabled], [_propsEnabledO, _refEnabledO]) => {
      const upProps = _propsEnabled !== _propsEnabledO
      const upRef = _refEnabled !== _refEnabledO
      const _paginatorEnabled = upProps ? _propsEnabled : _refEnabled

      if (_propsEnabled === _refEnabled)
        if (_paginatorEnabled) {

          if (dataCount.value && pageNumber.value * pageSize.value > dataCount.value)
            emitLoad(pageSize.value, 0, false)
          else
            emitLoad(pageSize.value, pageNumber.value * pageSize.value, false)
        } else {
          pageNumber.value = 1
          reload(1, pageSize.value)
        }

      if (upProps) paginatorEnabled.value = _propsEnabled
      if (upRef) emit('update:enabled', _refEnabled)
    })

    watch(pageSize, s => {
      if (!paginatorEnabled.value) return
      emitLoad(s, 0, false)
    })

    watch([pageNumber, () => props.count], ([_p, _c], [pO, c0]) => {

      if (DEBUG_LOG)
        console.debug(`watch pageNumber ${_p}(${pO}) dataCount ${_c}(${c0})`)

      if (_c === 0 && init.value == null) { init.value = false; return; }

      if (_c === undefined || _c === null) {
        pageNumber.value = 1
        reload(1, pageSize.value)
      } else if (isNaN(_c)) {
        reload(1, pageSize.value * _p)
      } else if (pO !== _p) {
        emitLoad(pageSize.value, (_p - 1) * pageSize.value, !paginatorEnabled.value || !pageSize.value)
      }
    }, { immediate: true, deep: true })

    const maxHeight = ref<string>(props.maxHeight)
    if (!maxHeight.value) {
      const rootabsolute = useTemplateRef<HTMLDivElement>('rootabsolute')
      function checkMaxHeight() {
        if (rootabsolute.value?.scrollHeight && window.innerHeight) {
          const _maxHeight = Math.min(90, (rootabsolute.value?.scrollHeight / window.innerHeight) * 100)
          maxHeight.value = _maxHeight.toFixed(0) + 'vh'
          if (DEBUG_LOG)
            console.debug(`checkMaxHeight ${maxHeight.value} - scrollHeight: ${rootabsolute.value?.scrollHeight} && window.innerHeight: ${window.innerHeight}`)
        } else if (DEBUG_LOG) {
          console.debug(`checkMaxHeight undefined - scrollHeight: ${rootabsolute.value?.scrollHeight} && window.innerHeight: ${window.innerHeight}`)
        }
      }
      onMounted(() => checkMaxHeight())
    }

    return {
      maxHeight,
      increasePageSize,

      pageNumber,
      pageSize,

      paginatorEnabled,

      loading,
      dataCount,
      init,

      firstload() {
        emitLoad(pageSize.value, 0, false)
      },

      onInfiniteScroll: debounce((entries => {
        if (!entries.every(entry => entry.isIntersecting)) return

        emit('infinitescroll')

        if (loading.value) return
        if (increasePageSize.value) return
        if (!pageSize.value) return
        if (!props.count) return

        if (dataCount.value < props.count)
          pageNumber.value = pageNumber.value + 1
      }) as IntersectionObserverCallback),

      onResize: debounce(() => { if (!increasePageSize.value) checkPageSize() }, { delay: 500 })
    }
  }
})
</script>

<style lang="postcss">
.paginator {
  @apply h-full;

  >.absolute {
    @apply overflow-y-hidden max-h-screen max-w-screen top-0;
  }

  >div {
    @apply min-h-full h-full;

    >table {
      @apply relative w-full bg-white;
      border-collapse: collapse;

      >thead {
        @apply sticky top-0 z-20 bg-white;
      }

      >tbody {
        @apply w-full h-full;

        .loader {
          height: 15px;
          aspect-ratio: 5;
          --_g: no-repeat radial-gradient(farthest-side, #E5E7EB 94%, #0000);
          background: var(--_g), var(--_g), var(--_g), var(--_g);
          background-size: 20% 100%;
          animation:
            l40-1 .75s infinite alternate,
            l40-2 1.5s infinite alternate;
        }

        @keyframes l40-1 {

          0%,
          10% {
            background-position: 0 0, 0 0, 0 0, 0 0
          }

          33% {
            background-position: 0 0, calc(100%/3) 0, calc(100%/3) 0, calc(100%/3) 0
          }

          66% {
            background-position: 0 0, calc(100%/3) 0, calc(2*100%/3) 0, calc(2*100%/3) 0
          }

          90%,
          100% {
            background-position: 0 0, calc(100%/3) 0, calc(2*100%/3) 0, 100% 0
          }
        }

        @keyframes l40-2 {

          0%,
          49.99% {
            transform: scale(1)
          }

          50%,
          100% {
            transform: scale(-1)
          }
        }
      }

      >tfoot {
        @apply sticky bottom-0 z-20 bg-white border-t-2 border-gray-200;

        nav[aria-label="pagination"] {
          button {
            &:not([disabled]) {
              @apply hover:bg-gray-50;
            }
          }
        }
      }
    }
  }
}
</style>